import { useState } from "react";
import {
  BarChart3,
  Clock,
  DollarSign,
  Filter,
  ShieldCheck,
  Users,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { DEAL_FLOW_PERMISSIONS } from "@/lib/dealFlowPermissions";
import { MetricsFilterProvider } from "./metrics/MetricsFilterContext";
import { MetricsFilterBar } from "./metrics/MetricsFilterBar";
import { PipelineMetricsSection } from "./metrics/PipelineMetricsSection";
import { VolumeMetricsSection } from "./metrics/VolumeMetricsSection";
import { QualityMetricsSection } from "./metrics/QualityMetricsSection";
import { FinancialMetricsSection } from "./metrics/FinancialMetricsSection";
import { AnalystLoadMetricsSection } from "./metrics/AnalystLoadMetricsSection";
import { TimeToCloseMetricsSection } from "./metrics/TimeToCloseMetricsSection";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";

// Metrics dashboard. Tabbed sub-navigation with
// shared filter state; Analyst Load tab hidden wholesale when the caller
// lacks DealFlow.ViewAllMetrics.

type TabId =
  | "pipeline"
  | "volume"
  | "quality"
  | "financial"
  | "analyst-load"
  | "time-to-close";

export function MetricsDashboard() {
  const { hasPermission } = useAuth();
  const canSeeAnalystLoad = hasPermission(DEAL_FLOW_PERMISSIONS.ViewAllMetrics);
  const [tab, setTab] = useState<TabId>("pipeline");

  // Semantically distinct lucide-react icons per sub-tab so the tab bar
  // is navigable at a glance: Pipeline=Filter (funnel), Volume=BarChart3
  // (stacked bars), Quality=ShieldCheck, Financial=DollarSign,
  // Analyst Load=Users, Time to Close=Clock.
  const items: EntityTabItem<TabId>[] = [
    { key: "pipeline", label: "Pipeline", icon: Filter },
    { key: "volume", label: "Volume", icon: BarChart3 },
    { key: "quality", label: "Quality", icon: ShieldCheck },
    { key: "financial", label: "Financial", icon: DollarSign },
    ...(canSeeAnalystLoad
      ? [{ key: "analyst-load" as const, label: "Analyst Load", icon: Users }]
      : []),
    { key: "time-to-close", label: "Time to Close", icon: Clock },
  ];

  return (
    <MetricsFilterProvider>
      <div className="flex flex-col gap-space-4">
        <PageHeader
          eyebrow="Deal Flow"
          title="Metrics"
          description="Aggregated performance across the deal flow lifecycle."
        />
        <MetricsFilterBar />
        <EntityTabBar
          ariaLabel="Metrics dashboard sections"
          testIdPrefix="metrics"
          items={items}
          value={tab}
          onChange={setTab}
        />
        <div className="pt-space-3">
          {tab === "pipeline" && <PipelineMetricsSection />}
          {tab === "volume" && <VolumeMetricsSection />}
          {tab === "quality" && <QualityMetricsSection />}
          {tab === "financial" && <FinancialMetricsSection />}
          {tab === "analyst-load" && canSeeAnalystLoad && <AnalystLoadMetricsSection />}
          {tab === "time-to-close" && <TimeToCloseMetricsSection />}
        </div>
      </div>
    </MetricsFilterProvider>
  );
}
